'use client';

import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import Link from '@eliadmin/core/src/components/link';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useState } from 'react';
import { FaCheck } from 'react-icons/fa';
import type { ProductAccessoryVariantRow } from '../product-accessory-variants-container';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';

export function ProductAccessoryMobileTable({
  variants,
}: {
  variants: ProductAccessoryVariantRow[];
}) {
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const currencySettings = useGlobalStore(state => state.generalLayout.currency_settings);
  const isWholesaler = useCustomerStore(state => state.me?.customer.is_wholesaler);
  const { t } = useTranslations();
  const [expandedVariants, setExpandedVariants] = useState<number[]>([]);

  const sortedVariants = variants.map(variant => ({
    ...variant,
    attributes: Array.isArray(variant.attributes)
      ? [...variant.attributes].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      : [],
  }));

  return (
    <div className="flex w-full flex-col sm:hidden">
      {sortedVariants.map(variant => {
        const isExpanded = expandedVariants.includes(variant.id);
        const priceDisplay = getProductPriceDisplay(variant, { isWholesaler });

        const parameterRows = [
          ...((Array.isArray(variant.attributes) ? variant.attributes : []).map(attribute => ({
            name: attribute.name,
            value:
              attribute.values
                ?.map(value => value.name)
                .filter(Boolean)
                .join(', ') || '--',
          })) ?? []),
        ];
        const visibleParameterRows = isExpanded ? parameterRows : parameterRows.slice(0, 3);
        const hasHiddenParameters = parameterRows.length > 3 && !isExpanded;

        const summaryRows = [
          {
            name: t('text164'),
            value: priceDisplay.priceOnRequest
              ? t('text533')
              : formatPrice(priceDisplay.currentWithVat, currency, currencySettings),
          },
          {
            name: t('text3605'),
            value: (
              <div className="flex items-center justify-start gap-2.5">
                <div
                  className={cn(
                    'flex min-h-5 min-w-5 items-center justify-center rounded-full',
                    variant.availability.id === 1 ? 'bg-[#57AE00]' : 'bg-[#FF0000]',
                  )}
                >
                  <FaCheck size={12} className="text-white" />
                </div>
                <p
                  className={cn(
                    'text-sm font-bold',
                    variant.availability.id === 1 ? 'text-[#57ae00]' : 'text-[#ff0000]',
                  )}
                >
                  {variant.availability.name}
                </p>
              </div>
            ),
          },
        ];

        const nameValue = variant.isCurrentProduct ? (
          <span
            className={cn(
              'w-full text-sm font-normal text-primary underline',
              'cursor-default font-bold',
            )}
          >
            {variant.name || variant.code}
          </span>
        ) : (
          <Link
            href={`/${variant.slug}`}
            prefetch={true}
            replace
            className={cn('w-full text-sm font-normal text-primary underline')}
          >
            {variant.name || variant.code}
          </Link>
        );

        return (
          <div
            key={variant.id}
            className={cn(
              'flex w-full flex-col items-start justify-start gap-4 border-b border-b-[#E9E9E9] px-2 pb-6 pt-6',
            )}
          >
            <div className="flex w-full items-start justify-start">{nameValue}</div>

            {summaryRows.map((row, index) => (
              <div
                key={`${variant.id}-summary-${index}`}
                className="flex w-full items-center justify-between gap-4"
              >
                <span className={cn('text-sm font-medium text-foreground')}>{row.name}</span>
                {typeof row.value === 'string' ? (
                  <p className={cn('text-right text-sm font-normal text-foreground')}>
                    {row.value}
                  </p>
                ) : (
                  row.value
                )}
              </div>
            ))}

            {parameterRows.length > 0 && (
              <div className="flex w-full flex-col gap-3">
                <span className="text-sm font-semibold text-foreground">{t('text9')}</span>

                {visibleParameterRows.map((row, index) => (
                  <div
                    key={`${variant.id}-parameter-${index}`}
                    className="flex w-full items-start justify-between gap-4"
                  >
                    <span className="max-w-[45%] text-sm font-medium text-foreground">
                      {row.name}
                    </span>
                    <p className="text-right text-sm font-normal text-foreground">{row.value}</p>
                  </div>
                ))}

                {hasHiddenParameters && (
                  <button
                    type="button"
                    className="w-fit text-sm font-semibold text-primary underline"
                    onClick={() => {
                      setExpandedVariants(current =>
                        current.includes(variant.id) ? current : [...current, variant.id],
                      );
                    }}
                  >
                    {t('text3516')} ({`+${parameterRows.length - 3}`})
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
